import axios from "axios";
import { meRoute, changeLocationRoute } from "../routes/apiRoutes";
import { login, logout } from "../Redux/authenticationSlice";
import { apply } from "../Redux/themeSlice";

const axiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

let lastLocationSyncAtMs = 0;
let inFlightLocationSync = null;

const shouldSkipLocationSync = (config) => {
  const url = String(config?.url || "");
  return (
    config?.headers?.["x-skip-location-sync"] === "true" ||
    url.includes(meRoute) ||
    url.includes(changeLocationRoute)
  );
};

async function syncLocationIfNeeded(store) {
  const token = localStorage.getItem("token");
  if (!token) return;

  // Throttle: don't hit /me on every request.
  const now = Date.now();
  if (now - lastLocationSyncAtMs < 8000) return;

  if (inFlightLocationSync) return inFlightLocationSync;

  inFlightLocationSync = (async () => {
    try {
      // Use a bare axios call to avoid interceptor recursion.
      const authHeaders = { Authorization: `Bearer ${token}`, "x-skip-location-sync": "true" };
      const meRes = await axios.get(meRoute, { headers: authHeaders });
      let safeUser = meRes?.data?.data?.user;

      if (!safeUser) return;

      // If current location is no longer allowed, switch to first allowed location.
      const currentId = safeUser?.currentLocation?.id;
      const allowedIds = Array.isArray(safeUser?.locationIds) ? safeUser.locationIds : [];
      if (currentId && allowedIds.length > 0 && !allowedIds.includes(currentId)) {
        const fallbackId = allowedIds[0];
        await axios.post(
          changeLocationRoute,
          { locationId: fallbackId },
          { headers: authHeaders }
        );
        const meRes2 = await axios.get(meRoute, { headers: authHeaders });
        safeUser = meRes2?.data?.data?.user || safeUser;
      }

      const state = store.getState();
      const currentUser = state?.authenticationValues?.currentUser || {};
      const localCurrentId =
        currentUser?.currentLocation?.id ??
        currentUser?.currentLocationId ??
        localStorage.getItem("currentLocationId");

      const nextCurrentId = safeUser?.currentLocation?.id ?? safeUser?.currentLocationId ?? null;
      const isSuperAdmin =
        Boolean(currentUser?.isSuperAdmin) ||
        (Array.isArray(currentUser?.roles) &&
          currentUser.roles.some((role) => role?.name === "Super Admin")) ||
        (Array.isArray(currentUser?.userRoles) &&
          currentUser.userRoles.some((ur) => ur?.role?.name === "Super Admin"));
      const allCentersSelected =
        isSuperAdmin && !currentUser?.currentLocationId && !currentUser?.currentLocation?.id;
      if (
        !allCentersSelected &&
        nextCurrentId &&
        String(nextCurrentId) !== String(localCurrentId || "")
      ) {
        // Update Redux + localStorage immediately so subsequent requests & UI use the new location.
        const avatarBase64 = localStorage.getItem("avatar") || "";
        store.dispatch(login({ user: safeUser, token: null, avatar: avatarBase64 }));

        const colors = state?.themeValues?.colors;
        store.dispatch(
          apply({
            colors,
            mainColor: safeUser?.currentLocation?.colorPalette || "#BB2436",
            mode: "light",
          })
        );
      }

      lastLocationSyncAtMs = Date.now();
    } catch (e) {
      // If token became invalid, keep existing behavior consistent with app routing.
      const status = e?.response?.status ?? e?.response?.data?.statusCode;
      if (status === 401) {
        store.dispatch(logout());
      }
    } finally {
      inFlightLocationSync = null;
    }
  })();

  return inFlightLocationSync;
}

export function setupAxiosInterceptors(store) {
  // Prevent double-registration in HMR/dev.
  if (axiosInstance.__locationSyncSetup) return;
  axiosInstance.__locationSyncSetup = true;

  axiosInstance.interceptors.request.use(
    async (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      if (store && !shouldSkipLocationSync(config)) {
        await syncLocationIfNeeded(store);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}

/* axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const statusCode = error.response ? error.response.status : null;

    if (error.message === "Network Error") {
      toast.error(
        "Unable to reach the server. Please try again later or contact support if the issue persists.",
        {
          autoClose: false,
        }
      );
    } else if (statusCode === 500) {
      toast.error("Server error. Please try again later.", {
        autoClose: false,
      });
    }
    return Promise.reject(error);
  }
); */

export default axiosInstance;


// import axios from "axios";
// import { toast } from "react-toastify";

// const axiosInstance = axios.create({
//   headers: {
//     'Content-Type': 'application/json',
//     'ngrok-skip-browser-warning': 'true',
//   },
// });

// // Request interceptor - add auth token automatically
// axiosInstance.interceptors.request.use(
//   (config) => {
//     const token = localStorage.getItem("token");
//     if (token) {
//       config.headers.Authorization = `Bearer ${token}`;
//     }
//     return config;
//   },
//   (error) => {
//     return Promise.reject(error);
//   }
// );

// // Response interceptor - handle common errors and transform response
// axiosInstance.interceptors.response.use(
//   (response) => {
//     console.log("RESPONSE INTERCEPTOR", response);
//     // Transform nested response structure
//     if (response.data?.data) {
//       response.data = response.data.data;
//     }
//     return response;
//   },
//   (error) => {
//     console.log("ERROR", error);
//     const { response, message } = error;
    
//     // Handle network errors
//     if (message === "Network Error") {
//       toast.error("Connection failed. Please check your internet.");
//     }
//     // Handle server errors
//     else if (response?.status >= 500) {
//       console.log("SERVER ERROR");
//       toast.error("Server error. Please try again later.");
//     }
//     // Handle auth errors
//     // else if (response?.status === 401) {
//     //   console.log("AUTH ERROR");
//     //   localStorage.removeItem("token");
//     //   window.location.href = "/login";
//     // }
//     // Handle other errors
//     else if (response?.data?.message) {
//       console.log("OTHER ERROR");
//       toast.error(response.data.message);
//     }
    
//     return Promise.reject(error);
//   }
// );

// export default axiosInstance;
